
import type { ExistingAttachment } from '../../../components/AttachmentManager'

export const compareSizes = (a: string, b: string): number => {
  const isNumA = /^\d+$/.test(a);
  const isNumB = /^\d+$/.test(b);

  if (isNumA && isNumB) {
    return Number(a) - Number(b);
  }
  if (isNumA && !isNumB) {
    return -1;
  }
  if (!isNumA && isNumB) {
    return 1;
  }

  const textOrder = [
    'PP', 'P', 'M', 'G', 'GG', 'XL', 'XLL',
    'G1', 'G2', 'G3', 'G4',
    'TAMANHO ÚNICO', 'ÚNICO', 'U'
  ];

  const normA = a.trim().toUpperCase();
  const normB = b.trim().toUpperCase();

  const idxA = textOrder.indexOf(normA);
  const idxB = textOrder.indexOf(normB);

  if (idxA !== -1 && idxB !== -1) {
    return idxA - idxB;
  }
  if (idxA !== -1 && idxB === -1) {
    return -1;
  }
  if (idxA === -1 && idxB !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};


export interface FashionOrderProductItemInput {
  colorId: number
  size: string
  quantity: number
}

export type SizeQuantities = { [size: string]: number }

export interface ProductApiData {
  fashionProductId?: number
  discount: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionProduct?: {
    id: number
    name: string
    price: number
    group: { id: number; name?: string; family?: { id: number; name?: string } }
  }
  colorCount?: number
  family?: { id: number; name?: string }
  group?: { id: number; name?: string }
  sizeQuantities?: SizeQuantities
  kitIndex?: number | null
  colors?: string[]
  selectedColors?: number[]
}

export interface CompanyItemType {
  id: number
  companyName: string
}

export interface UserItemType {
  id: number
  name: string
}

export type FormViewState = {
  supplier: CompanyItemType & {
    cnpj?: string
    telefone?: string
    email?: string
  }
  seller: UserItemType
  company: CompanyItemType & {
    cnpj?: string
    inscricaoEstadual?: string
    endereco?: string
    bairro?: string
    cep?: string
    cidade?: string
    estado?: string
    telefone?: string
    email?: string
    nomeFantasia?: string
  }
  orderType: string
  paymentCondition: string
  shippingType: string
  trackingCode: string
  finalDateOfDelivery: string
  startDateOfDelivery: string
  extraInformation: string
  quantityItems: string
  quantityPieces: string
  totalCost: string
  shippingCost: string
  maximumBillingAmount: string
  carrier?: CompanyItemType | null
}

export const formatDate = (iso: string | null | undefined): string =>
  iso ? iso.substring(0, 10) : ''

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

export const formatMaximumBillingDisplay = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return formatCurrency(Number(digits) / 100)
}

export const countColors = (product: ProductApiData): number => {
  if (product.colorCount && product.colorCount > 0) return product.colorCount
  if (product.items?.length) return new Set(product.items.map((i) => i.colorId)).size
  return 0
}

export function normalizeProductData(product: Record<string, unknown>): ProductApiData {
  const sizeQuantities: SizeQuantities = {}
  const items = (product.items || []) as FashionOrderProductItemInput[]
  items.sort((a, b) => compareSizes(a.size, b.size))
  items.forEach((item) => {
    if (item.size && item.quantity !== undefined)
      sizeQuantities[item.size] = (sizeQuantities[item.size] || 0) + Number(item.quantity)
  })
  const totalItems = Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0)
  const fp = product.fashionProduct as ProductApiData['fashionProduct']
  const subtotal =
    (product.subtotal as number | undefined) ?? totalItems * (fp?.price || 0)
  const colorCount =
    (product.colorCount as number | undefined) || new Set(items.map((i) => i.colorId)).size

  return {
    ...(product as unknown as ProductApiData),
    sizeQuantities,
    totalItems,
    subtotal,
    colorCount,
    items,
    kitIndex: product.kitIndex !== undefined ? (product.kitIndex as number | null) : null,
  }
}

export function getTotalPieces(product: ProductApiData): number {
  return product.sizeQuantities
    ? Object.values(product.sizeQuantities).reduce((s, q) => s + q, 0)
    : product.totalItems ?? 0
}

export function formatProductDiscountLabel(product: ProductApiData): string {
  const clampedDiscount = Math.min(100, Math.max(0, product.discount ?? 0))
  if (!clampedDiscount || clampedDiscount <= 0) return '-'
  const original = (product.subtotal || 0) / (1 - clampedDiscount / 100)
  return `${clampedDiscount}% (${formatCurrency((original * clampedDiscount) / 100)})`
}

export interface FashionOrderDetailMapped {
  formState: FormViewState
  addedProducts: ProductApiData[]
  kitCount: number
  attachments: ExistingAttachment[]
}

export function mapSteelOrderResponseToDetail(data: Record<string, unknown>): FashionOrderDetailMapped {
  const getLabelMain = (item: any): string => {
    if (!item || !item.isMain) return '';
    const tipo = item.tipoEstabelecimento || (item.cnpj ? (item.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
    return tipo ? ` - ${tipo}` : '';
  };

  const supDetails = (data.supplierDetails as Record<string, any>) || {}
  const cliDetails = (data.clientDetails as Record<string, any>) || {}

  const formState: FormViewState = {
    supplier: {
      id: Number(data.supplierId),
      companyName: `${(data.supplierDetails as { name?: string } | undefined)?.name || String(data.supplier || '')}${getLabelMain(data.supplierDetails)}`,
      cnpj: supDetails.cnpj || supDetails.document || '',
      telefone: supDetails.telefone || supDetails.phone || supDetails.telephone || '',
      email: supDetails.email || '',
    },
    seller: {
      id: (data.sellerId as number) || 0,
      name: (data.sellerDetails as { name?: string } | undefined)?.name || String(data.seller || ''),
    },
    company: {
      id: Number(data.clientId),
      companyName: `${(data.clientDetails as { name?: string } | undefined)?.name || String(data.client || '')}${getLabelMain(data.clientDetails)}`,
      cnpj: cliDetails.cnpj || cliDetails.document || '',
      inscricaoEstadual: cliDetails.inscricaoEstadual || cliDetails.stateInscription || cliDetails.ie || '',
      endereco: cliDetails.endereco || cliDetails.address || cliDetails.logradouro || '',
      bairro: cliDetails.bairro || cliDetails.neighborhood || '',
      cep: cliDetails.cep || cliDetails.zipCode || '',
      cidade: cliDetails.cidade || cliDetails.city || '',
      estado: cliDetails.estado || cliDetails.state || cliDetails.uf || '',
      telefone: cliDetails.telefone || cliDetails.phone || cliDetails.telephone || '',
      email: cliDetails.email || '',
      nomeFantasia: cliDetails.nomeFantasia || cliDetails.fantasyName || cliDetails.name || '',
    },
    orderType: String(data.orderType || ''),
    paymentCondition: String(
      (data.paymentTerm as string | undefined) ||
        (typeof data.paymentCondition === 'object' && data.paymentCondition !== null
          ? (data.paymentCondition as { name?: string }).name
          : (data.paymentCondition as string | undefined)) ||
        '',
    ),
    shippingType:
      typeof data.shippingType === 'object' && data.shippingType !== null
        ? String((data.shippingType as { name?: string }).name || '')
        : String(data.shippingType || ''),
    trackingCode: String(data.trackingCode || ''),
    finalDateOfDelivery: formatDate(data.finalDateOfDelivery as string | undefined),
    startDateOfDelivery: formatDate(
      (data.startDateOfDelivery as string | undefined) || (data.orderDate as string | undefined),
    ),
    extraInformation: String(data.extraInfo || data.extraInformation || ''),
    quantityItems: String(data.quantityItems || '0'),
    quantityPieces: String(data.quantityPieces || '0'),
    totalCost: `R$ ${Number(data.totalCost || 0)
      .toFixed(2)
      .replace('.', ',')}`,
    shippingCost:
      data.shippingValue != null
        ? formatCurrency(Number(data.shippingValue))
        : data.shippingCost != null
          ? String(data.shippingCost)
          : '',
    maximumBillingAmount: formatMaximumBillingDisplay(data.maximumBillingAmount),
    carrier: data.carrier ? { id: Number((data.carrier as any).id), companyName: (data.carrier as any).companyName || '' } : null,
  }

  const attachments: ExistingAttachment[] = ((data.attachments || []) as Record<string, unknown>[]).map((a) => ({
    id: Number(a.id),
    originalName: String(a.originalName || a.title || a.fileName || 'Arquivo'),
    fileName: String(a.fileName || a.title || ''),
    filePath: String(a.fileUrl || a.filePath || ''),
    fileSize: Number(a.fileSize) || 0,
    mimeType: String(a.mimeType || ''),
  }))

  const products = ((data.items || []) as Record<string, unknown>[])
    .filter((item) => item.itemType === 'PRODUCT')
    .map((item) => {
      let extra: Record<string, unknown> = {}
      try {
        extra = item.extraInfo ? JSON.parse(String(item.extraInfo)) : {}
      } catch {
        extra = {}
      }
      const P = item.Product as
        | {
            Family?: { id?: number; name?: string }
            family?: { id?: number; name?: string }
            group?: { id?: number; name?: string }
            name?: string
            price?: number
          }
        | undefined
      const famRel = P?.Family || P?.family
      const famId = Number(extra.familyId ?? famRel?.id ?? 0)
      const famName = String(extra.familyName || famRel?.name || '').trim()

      return normalizeProductData({
        fashionProductId: item.productId,
        fashionProduct: {
          id: item.productId as number,
          name: String(extra.fashionProductName || P?.name || ''),
          price: Number(extra.fashionProductPrice || P?.price || 0),
          group: {
            id: Number(extra.groupId || P?.group?.id),
            name: String(extra.groupName || P?.group?.name || ''),
            family: {
              id: famId,
              name: famName,
            },
          },
        },
        family: famName ? { id: famId, name: famName } : undefined,
        discount: (item.descount as number) || 0,
        totalItems: Number(item.quantity) || 0,
        subtotal: Number(item.priceKg) || 0,
        items: (extra.fashionItems as FashionOrderProductItemInput[]) || [],
        colorCount: Number(extra.colorCount) || 0,
        kitIndex: item.kitIndex !== undefined ? (item.kitIndex as number | null) : null,
        colors: (extra.colors as string[]) || [],
        selectedColors: (extra.selectedColors as number[]) || [],
      })
    })

  const maxKit = products.reduce((max: number, p: ProductApiData) => {
    if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > max) return p.kitIndex
    return max
  }, -1)
  const kitCount = maxKit >= 0 ? maxKit + 1 : 0

  return { formState, addedProducts: products, kitCount, attachments }
}
